<script>
  import { onMount, getContext } from "svelte";
  import { some } from "overline/iterable";

  import { Modal } from "svelte-utilities";
  import FileIcon from "@local/assets/icons/file.svg";
  import FileIconPdf from "@local/assets/icons/file-pdf.svg";
  import SpinnerIcon from "@local/assets/icons/spinner-lg.svg";
  import FileIconMultiple from "@local/assets/icons/file-multiple.svg";
  import { WBPVersion, GSVersion } from "#src/stores/ui.js";
  import { downloadJobBundle } from "#src/extensions/download-job-bundle";
  import ConfirmGsUpdate from "#src/lib/ConfirmGsUpdate.svelte";
  import eb from "#src/extensions/event-bus.js";
  import { profile } from "#src/stores/auth.js";

  export let title = "Download Job Contents";
  export let group;
  export let types;
  export let name;
  export let rfq = null;
  export let quote = null;
  export const open = () => openModal;

  const edgeTreatments = getContext("edgeTreatments");

  let modal;
  let downloading = false;
  let allReportsExcelInput;
  let allReportsWBPInput;
  let allReportsGSInput;
  let allReportsPdfInput;
  let includeData = false;
  let includeCAD = false;
  let includeBOM = false;
  let includeWBP = false;
  let includeGS = false;
  let includeReportsExcel = [];
  let includeReportsWBP = [];
  let includeReportsGS = [];
  let includeReportsPdf = [];

  $: groupHasAttachments = hasAttachments($group) || hasDrawings($group);
  $: allPdfReportsSelected = areAllReportsSelected(includeReportsPdf, $group, allReportsPdfInput);
  $: allExcelReportsSelected = areAllReportsSelected(includeReportsExcel, $group, allReportsExcelInput);
  $: allWBPReportsSelected = areAllReportsSelected(includeReportsWBP, $group, allReportsWBPInput);
  $: allGSReportsSelected = areAllReportsSelected(includeReportsGS, $group, allReportsGSInput);
  $: disabled =
    (!includeData &&
      !includeCAD &&
      !includeBOM &&
      !includeWBP &&
      !includeGS &&
      !includeAttachments &&
      includeReportsExcel.every((x) => !x) &&
      includeReportsWBP.every((x) => !x) &&
      includeReportsGS.every((x) => !x) &&
      includeReportsPdf.every((x) => !x)) ||
    ((includeGS || includeReportsGS.some((x) => x)) && !$profile?.confirmed_gs_update_on);

  let includeAttachments = groupHasAttachments;

  function hasAttachments(group) {
    return group?.attachments?.length > 0;
  }

  function hasDrawings(group) {
    if (group && group.items) {
      return some(group.items, (item) => !!item.drawing);
    }
    return false;
  }

  async function openModal() {
    const itemReports = $group.item_reports.order.map((id) => $group.item_reports[id]);
    if (includeReportsPdf.length !== itemReports.length) {
      includeReportsPdf = itemReports.map(() => false);
    }
    if (includeReportsExcel.length !== itemReports.length) {
      includeReportsExcel = itemReports.map(() => false);
    }
    if (includeReportsWBP.length !== itemReports.length) {
      includeReportsWBP = itemReports.map(() => false);
    }
    if (includeReportsGS.length !== itemReports.length) {
      includeReportsGS = itemReports.map(() => false);
    }
    modal.open();
  }

  async function downloadBundle() {
    downloading = true;
    await downloadJobBundle(
      $group,
      types,
      { edgeTreatments: $edgeTreatments },
      {
        productQuote: $group?.project_type === "product",
        rfq,
        quote,
        filename: `${name}`,
        includeReportsExcel,
        includeReportsWBP,
        includeReportsGS,
        includeReportsPdf,
        includeData,
        includeCAD,
        includeAttachments,
        includeBOM,
        includeWBP,
        includeGS,
        wbpVersion: $WBPVersion,
        gsVersion: $GSVersion,
      },
      $profile,
    );
    modal.close();
    downloading = false;
  }

  function areAllReportsSelected(includeReports, grp, input) {
    let allSelected = true;
    let allUnselected = true;

    grp?.item_reports.order.forEach((_, index) => {
      const selected = includeReports[index];
      if (selected) {
        allUnselected = false;
      } else {
        allSelected = false;
      }
    });

    if (input) {
      if (allSelected || allUnselected) {
        input.indeterminate = false;
      } else {
        input.indeterminate = true;
      }
    }

    return allSelected;
  }

  function toggleSelectedPdfReports() {
    if (
      $group.item_reports.order.every((reportId, index) => {
        const report = $group.item_reports[reportId];
        return report?.group_by === "notes" || report?.group_by === "linear" || includeReportsPdf[index];
      })
    ) {
      includeReportsPdf = $group.item_reports.order.map(() => false);
    } else {
      includeReportsPdf = $group.item_reports.order.map((reportId) => {
        const report = $group.item_reports[reportId];
        return report?.group_by === "notes" || report?.group_by === "linear" ? false : true;
      });
    }
  }

  function toggleSelectedExcelReports() {
    if ($group.item_reports.order.every((_, index) => includeReportsExcel[index])) {
      includeReportsExcel = $group.item_reports.order.map(() => false);
    } else {
      includeReportsExcel = $group.item_reports.order.map(() => true);
    }
  }

  function toggleSelectedWBPReports() {
    if (
      $group.item_reports.order.every((reportId, index) => {
        const report = $group.item_reports[reportId];
        return report?.group_by !== "opening" || includeReportsWBP[index];
      })
    ) {
      includeReportsWBP = $group.item_reports.order.map(() => false);
    } else {
      includeReportsWBP = $group.item_reports.order.map((reportId) => {
        const report = $group.item_reports[reportId];
        return report?.group_by === "opening" ? true : false;
      });
    }
  }

  function toggleSelectedGSReports() {
    if (
      $group.item_reports.order.every((reportId, index) => {
        const report = $group.item_reports[reportId];
        return report?.group_by !== "opening" || includeReportsGS[index];
      })
    ) {
      includeReportsGS = $group.item_reports.order.map(() => false);
    } else {
      includeReportsGS = $group.item_reports.order.map((reportId) => {
        const report = $group.item_reports[reportId];
        return report?.group_by === "opening" ? true : false;
      });
    }
  }

  onMount(() => {
    eb.on("open-download", openModal);

    return () => {
      eb.unsubscribe("open-download", openModal);
    };
  });
</script>

<Modal
  bind:this={modal}
  width="30rem"
  closeable
  on:confirm={downloadBundle}
  buttons={[
    { label: "Cancel", type: "cancel" },
    { label: "Download", type: "confirm", style: "primary", closeOnClick: false, disabled },
  ]}>
  <div slot="title">{title}</div>
  <div slot="content" class="space-y-4">
    {#if downloading}
      <div class="flex gap-2 items-center">
        <div>Downloading...</div>
        <div class="relative w-8 h-8">
          <div class="absolute">
            <div class="animate-spin">
              <SpinnerIcon />
            </div>
          </div>
        </div>
      </div>
    {:else}
      <div class="font-bold">Reports:</div>
      <div class="text-xs border-b border-gray-300 pb-4 px-2">
        <table class="w-full table-auto">
          <thead>
            <tr>
              <th></th>
              <th class="px-2 text-center">Excel</th>
              <th class="px-2 text-center">WinBidPro</th>
              <th class="px-2 text-center">GlazierStudio</th>
              <th class="px-2 text-center">PDF</th>
            </tr>
          </thead>
          <tbody>
            {#if $group.item_reports.order.length > 1}
              <tr>
                <td class="italic font-bold">All Reports</td>
                <td class="px-2 text-center">
                  <input
                    type="checkbox"
                    bind:this={allReportsExcelInput}
                    checked={allExcelReportsSelected}
                    on:input={toggleSelectedExcelReports} />
                </td>
                <td class="px-2 text-center">
                  <input
                    type="checkbox"
                    bind:this={allReportsWBPInput}
                    checked={allWBPReportsSelected}
                    on:input={toggleSelectedWBPReports} />
                </td>
                <td class="px-2 text-center">
                  <input
                    type="checkbox"
                    bind:this={allReportsGSInput}
                    checked={allGSReportsSelected}
                    on:input={toggleSelectedGSReports} />
                </td>
                <td class="px-2 text-center">
                  <input
                    bind:this={allReportsPdfInput}
                    type="checkbox"
                    checked={allPdfReportsSelected}
                    on:input={toggleSelectedPdfReports} />
                </td>
              </tr>
            {/if}
            {#each $group.item_reports.order as reportId, index (reportId)}
              {@const report = $group.item_reports[reportId]}
              <tr>
                <td>{report.report_name || "Report"}</td>
                <td class="px-2 text-center">
                  <input type="checkbox" bind:checked={includeReportsExcel[index]} />
                </td>
                <td class="px-2 text-center">
                  {#if report?.group_by === "opening"}
                    <input type="checkbox" bind:checked={includeReportsWBP[index]} />
                  {/if}
                </td>
                <td class="px-2 text-center">
                  {#if report?.group_by === "opening"}
                    <input type="checkbox" bind:checked={includeReportsGS[index]} />
                  {/if}
                </td>
                <td class="px-2 text-center">
                  {#if report?.group_by !== "notes" && report?.group_by !== "linear"}
                    <input type="checkbox" bind:checked={includeReportsPdf[index]} />
                  {/if}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
      <div class="font-bold">Overall Job Downloads:</div>
      <div class="flex gap-2 justify-center items-center text-xs">
        <div class="flex flex-col items-center w-16 gap-2">
          <FileIcon />
          <div>Excel</div>
          <input type="checkbox" bind:checked={includeData} />
        </div>
        <div class="flex flex-col items-center w-16 gap-2">
          <FileIconMultiple />
          <div>CAD</div>
          <input type="checkbox" bind:checked={includeCAD} />
        </div>
        <div class="flex flex-col items-center w-16 gap-2">
          <FileIcon />
          <div>BOM</div>
          <input type="checkbox" bind:checked={includeBOM} />
        </div>
        <div class="flex flex-col items-center w-16 gap-2">
          <FileIcon />
          <div>WBP</div>
          <input type="checkbox" bind:checked={includeWBP} />
        </div>
        <div class="flex flex-col items-center w-16 gap-2">
          <FileIcon />
          <div>GS</div>
          <input type="checkbox" bind:checked={includeGS} />
        </div>
        {#if groupHasAttachments}
          <div class="flex flex-col items-center w-16 gap-2">
            <FileIconMultiple />
            <div>Attachments</div>
            <input type="checkbox" bind:checked={includeAttachments} disabled={!groupHasAttachments} />
          </div>
        {/if}
      </div>
      {#if (includeGS || includeReportsGS.some((x) => x)) && !$profile?.confirmed_gs_update_on}
        <ConfirmGsUpdate />
      {/if}
    {/if}
  </div>
</Modal>
